import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { FinanceReportRow, ReportsApi } from '@/services/reports.api';
import { AcademicYearsApi } from '@/services/academic.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatRupees(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Finance Report — income/expense/balance grouped by session year + month,
 * matching legacy's real shape (migrate-32-finance.ts migrated `incomes`/
 * `expenses`, both confirmed genuinely thin real data — see
 * docs/data-issues.md). A month with expense data but no income rows still
 * shows (Total Income = ₹0.00) — accurate, not a bug. Most real schools
 * have zero rows here; that's expected, not broken.
 */
export function FinanceReportPage() {
  const [yearId, setYearId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-finance-report'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['reports', 'finance', yearId],
    queryFn: () => ReportsApi.financeReport({ academicYearId: yearId || undefined }),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await ReportsApi.exportFinanceReport(format, { academicYearId: yearId || undefined });
    } finally {
      setBusy(null);
    }
  };

  const totals = rows.reduce(
    (acc, r) => ({ income: acc.income + r.totalIncome, expense: acc.expense + r.totalExpense, balance: acc.balance + r.balance }),
    { income: 0, expense: 0, balance: 0 },
  );

  return (
    <>
      <PageHeader
        title="Finance Report"
        description="Income, expense, and balance by session year and month."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={yearId} onChange={(e) => setYearId(e.target.value)}>
          <option value="">All Years</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Year</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Month</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Income</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Expense</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    No income or expense entries for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: FinanceReportRow, i) => (
                  <tr key={`${r.academicYearId}-${r.month}-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{r.schoolName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.academicYearName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{MONTH_NAMES[r.month - 1] ?? r.month}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{formatRupees(r.totalIncome)}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{formatRupees(r.totalExpense)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${r.balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {formatRupees(r.balance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-50 font-semibold">
                <tr>
                  <td className="px-4 py-3 text-slate-900" colSpan={3}>
                    Total
                  </td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatRupees(totals.income)}</td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatRupees(totals.expense)}</td>
                  <td className={`px-4 py-3 text-right ${totals.balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {formatRupees(totals.balance)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}
